'use client'

import { useLenis } from 'lenis/react'
import { useEffect } from 'react'
import * as CookieConsent from 'vanilla-cookieconsent'

import { useLocale } from '@/lib/i18n/useLocale'
import { syncTrackingConsent } from '@/lib/tracking/syncConsent'

import { createCookieConsentConfig } from './config'
import { notifyCookieConsentChange } from './consentStore'
import { setCookieConsentScrollLockHandlers } from './scrollLock'

let initializationPromise: Promise<void> | null = null

function initializeCookieConsent(locale: ReturnType<typeof useLocale>): Promise<void> {
  if (!initializationPromise) {
    initializationPromise = CookieConsent.run(
      createCookieConsentConfig(locale, notifyCookieConsentChange),
    )
  }

  return initializationPromise
}

export function CookieConsentManager() {
  const locale = useLocale()
  const lenis = useLenis()

  useEffect(() => {
    setCookieConsentScrollLockHandlers({
      lock: () => {
        lenis?.stop()
      },
      unlock: () => {
        lenis?.start()
      },
    })

    let isCurrent = true
    let cancelSchedule: (() => void) | null = null

    const runInit = () => {
      void initializeCookieConsent(locale)
        .then(async () => {
          if (!isCurrent) return

          await CookieConsent.setLanguage(locale, true)
          notifyCookieConsentChange()
          syncTrackingConsent()
        })
        .catch((error: unknown) => {
          console.error('Cookie consent manager failed to initialize.', error)
        })
    }

    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      const idleId = window.requestIdleCallback(runInit, { timeout: 1500 })
      cancelSchedule = () => window.cancelIdleCallback(idleId)
    } else {
      const timerId = setTimeout(runInit, 500)
      cancelSchedule = () => clearTimeout(timerId)
    }

    return () => {
      isCurrent = false
      cancelSchedule?.()
      setCookieConsentScrollLockHandlers(null)
    }
  }, [locale, lenis])

  return null
}
